/**
 * Application Header Component
 *
 * Top navigation bar with app title, user menu, and actions
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { APP_NAME, ROUTES } from '../../config/constants';
import { formatUserName, getUserInitials } from '../../utils/formatters';
import api from '../../services/api';
import './AppHeader.css';

const DEV_TOOLS = process.env.REACT_APP_DEV_TOOLS === 'true';
const ALL_ROLES = ['admin', 'developer', 'approver', 'user'];

const AppHeader = ({ user, onRoleChange }) => {
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const handleDevRoleSwitch = async (role) => {
    try {
      await api.devSetMyRole(role);
      onRoleChange && onRoleChange(role);
      setShowUserMenu(false);
    } catch (err) {
      console.error('Failed to switch role:', err);
    }
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await oktaAuth.revokeAccessToken();
    } catch {
      // Non-fatal — proceed with local token clear
    }
    await oktaAuth.tokenManager.clear();
    history.push(ROUTES.LOGIN);
  };

  const userInitials = getUserInitials(user);
  const userName = formatUserName(user);
  const ROLE_LABELS = { admin: 'Admin', developer: 'Developer', approver: 'Approver', user: 'Requestor' };
  const userRole = ROLE_LABELS[user?.role] || 'Requestor';

  return (
    <header className="app-header">
      <div className="app-header__container">
        {/* Logo and Title */}
        <div className="app-header__brand">
          <div className="app-header__logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <defs>
                <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#e3f2fd', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#90caf9', stopOpacity: 1}} />
                </linearGradient>
              </defs>
              <rect width="32" height="32" rx="6" fill="url(#skyGradient)" />
              {/* Rocket body */}
              <path d="M16 4 L19 10 L19 18 L13 18 L13 10 Z" fill="#e94560" />
              {/* Rocket nose cone */}
              <path d="M16 4 L19 10 L13 10 Z" fill="#ff6b6b" />
              {/* Window */}
              <circle cx="16" cy="13" r="2" fill="#87ceeb" stroke="#4a90e2" strokeWidth="0.5" />
              {/* Left fin */}
              <path d="M13 16 L10 20 L13 20 Z" fill="#4a69bd" />
              {/* Right fin */}
              <path d="M19 16 L22 20 L19 20 Z" fill="#4a69bd" />
              {/* Flames - orange */}
              <path d="M14 18 L13 24 L15 20 Z" fill="#ffa502">
                <animate attributeName="opacity" values="1;0.6;1" dur="0.3s" repeatCount="indefinite" />
              </path>
              {/* Flames - yellow */}
              <path d="M16 18 L15.5 26 L16.5 26 Z" fill="#ffd32a">
                <animate attributeName="opacity" values="1;0.7;1" dur="0.25s" repeatCount="indefinite" />
              </path>
              {/* Flames - red */}
              <path d="M18 18 L17 20 L19 24 Z" fill="#ff4757">
                <animate attributeName="opacity" values="1;0.6;1" dur="0.35s" repeatCount="indefinite" />
              </path>
            </svg>
          </div>
          <div className="app-header__title-group">
            <h1 className="app-header__title">{APP_NAME}</h1>
            <span className="app-header__tagline">Made by Guidewire, for Guidewire</span>
          </div>
        </div>

        {/* User Menu */}
        {user && (
          <div className="app-header__user">
            <button
              className="app-header__user-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
              <div className="app-header__avatar">{userInitials}</div>
              <div className="app-header__user-info">
                <div className="app-header__user-name">{userName}</div>
                <div className="app-header__user-role">{userRole}</div>
              </div>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`app-header__chevron ${showUserMenu ? 'open' : ''}`}
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="app-header__dropdown">
                <div className="app-header__dropdown-header">
                  <div className="app-header__dropdown-name">{userName}</div>
                  <div className="app-header__dropdown-email">{user.email}</div>
                </div>
                {user.role === 'admin' && (
                  <>
                    <div className="app-header__dropdown-divider" />
                    <button
                      className="app-header__dropdown-item"
                      onClick={() => {
                        setShowUserMenu(false);
                        history.push(ROUTES.ADMIN);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                      </svg>
                      Admin Dashboard
                    </button>
                  </>
                )}
                {DEV_TOOLS && (
                  <>
                    <div className="app-header__dropdown-divider" />
                    <div className="app-header__dropdown-section-label">Switch Role</div>
                    {ALL_ROLES.map(role => (
                      <button
                        key={role}
                        className={`app-header__dropdown-item${user?.role === role ? ' app-header__dropdown-item--active' : ''}`}
                        onClick={() => handleDevRoleSwitch(role)}
                        disabled={user?.role === role}
                      >
                        {ROLE_LABELS[role]}
                        {user?.role === role && ' ✓'}
                      </button>
                    ))}
                  </>
                )}
                <div className="app-header__dropdown-divider" />
                <button
                  className="app-header__dropdown-item app-header__dropdown-item--danger"
                  onClick={handleLogout}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M3 3a1 1 0 011-1h5a1 1 0 011 1v1h-1V3H4v10h5v-1h1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V3z" />
                    <path d="M11.293 5.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L12.586 10H6a1 1 0 110-2h6.586l-1.293-1.293a1 1 0 010-1.414z" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

AppHeader.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    role: PropTypes.string,
  }),
  onRoleChange: PropTypes.func,
};

export default AppHeader;
